import { useCallback, useMemo, useRef, useEffect } from "react";

// Hook de sons curtos via Web Audio API. Sem precisar de arquivo .mp3
// — tudo gerado em tempo real com oscillator. Estilo Uber/iFood:
// alerta vivo pra chamadas, ding curto pra confirmacoes, beep grave
// pra cancelamentos/erros.
//
// Uso:
//   const som = useNotificacaoSom();
//   som.tocar('oferta');      // chamada nova (oscillator)
//   som.tocar('oferta_voz');  // chamada nova com VOZ .mp3 (fallback: oscillator 'oferta')
//   som.tocar('confirmar');   // pedido aceito
//   som.tocar('cancelar');    // recusado/cancelado
//   som.tocar('ding');        // ding curto generico
//
// User pode silenciar globalmente:
//   localStorage.setItem('toalugando_som_off', '1')

// Voz gravada da marca pra chamada de corrida (estilo Uber). Servida do
// /public — CSP bloqueia CDN, então o arquivo é LOCAL de proposito.
const SOM_OFERTA_VOZ_URL = "/sons/oferta-voz-feminina.mp3";

const PADROES = {
  // [freq Hz, duracao ms, tipo] sequencias
  oferta: [
    [880, 120, "sine"],
    [0, 80, ""],
    [880, 120, "sine"],
    [0, 80, ""],
    [1100, 200, "sine"],
  ],
  confirmar: [
    [660, 100, "sine"],
    [880, 150, "sine"],
  ],
  cancelar: [
    [440, 200, "square"],
    [330, 250, "square"],
  ],
  ding: [
    [988, 100, "sine"],
  ],
  alerta: [
    [1200, 80, "triangle"],
    [0, 40, ""],
    [1200, 80, "triangle"],
    [0, 40, ""],
    [1200, 200, "triangle"],
  ],
};

export const useNotificacaoSom = () => {
  const ctxRef = useRef(null);

  const garantirContext = useCallback(() => {
    if (ctxRef.current) return ctxRef.current;
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return null;
      ctxRef.current = new Ctx();
    } catch {
      return null;
    }
    return ctxRef.current;
  }, []);

  // Browser pausa AudioContext quando aba perde foco. Resume ao voltar.
  useEffect(() => {
    const handler = () => {
      if (document.visibilityState === "visible" && ctxRef.current?.state === "suspended") {
        ctxRef.current.resume().catch(() => {});
      }
    };
    document.addEventListener("visibilitychange", handler);
    return () => document.removeEventListener("visibilitychange", handler);
  }, []);

  const tocar = useCallback((padrao = "ding", volume = 0.25) => {
    // Mute global via localStorage
    try {
      if (localStorage.getItem("toalugando_som_off") === "1") return;
    } catch {}

    // Toca a sequencia no oscillator (Web Audio) — usado direto pelos
    // padroes sintetizados e como FALLBACK do 'oferta_voz'.
    const tocarOscilador = (nomePadrao) => {
      const sequencia = PADROES[nomePadrao] || PADROES.ding;
      const ctx = garantirContext();
      if (!ctx) return;
      if (ctx.state === "suspended") ctx.resume().catch(() => {});

      let t = ctx.currentTime;
      for (const [freq, durMs, tipo] of sequencia) {
        const dur = durMs / 1000;
        if (freq === 0) {
          t += dur;
          continue;
        }
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.frequency.value = freq;
        osc.type = tipo || "sine";
        osc.connect(gain);
        gain.connect(ctx.destination);
        // Envelope: rampa rapida pra evitar click + decay
        gain.gain.setValueAtTime(0.0001, t);
        gain.gain.exponentialRampToValueAtTime(volume, t + 0.005);
        gain.gain.exponentialRampToValueAtTime(0.0001, t + dur);
        osc.start(t);
        osc.stop(t + dur);
        t += dur;
      }
    };

    // Vibration API no celular (Android Chrome aceita; iOS nao tem)
    const vibrar = (nomePadrao) => {
      if (!navigator.vibrate) return;
      try {
        const padraoVibracao = {
          oferta: [100, 50, 100, 50, 200],
          oferta_voz: [100, 50, 100, 50, 200],
          confirmar: [80],
          cancelar: [150, 100, 150],
          alerta: [60, 40, 60, 40, 150],
          ding: [50],
        };
        navigator.vibrate(padraoVibracao[nomePadrao] || padraoVibracao.ding);
      } catch {}
    };

    // 'oferta_voz': voz gravada .mp3 (estilo Uber "corrida disponível").
    // Se o Audio falhar (arquivo ausente, autoplay bloqueado, browser sem
    // suporte), cai pro oscillator 'oferta' — nunca fica mudo.
    if (padrao === "oferta_voz") {
      try {
        const audio = new Audio(SOM_OFERTA_VOZ_URL);
        audio.volume = Math.min(1, Math.max(0, volume * 2));
        const p = audio.play();
        if (p && typeof p.catch === "function") {
          p.catch(() => tocarOscilador("oferta"));
        }
        vibrar("oferta_voz");
        return;
      } catch {
        // new Audio() indisponível — segue pro oscillator abaixo
        padrao = "oferta";
      }
    }

    tocarOscilador(padrao);
    vibrar(padrao);
  }, [garantirContext]);

  const mute = useCallback((on) => {
    try {
      if (on) localStorage.setItem("toalugando_som_off", "1");
      else localStorage.removeItem("toalugando_som_off");
    } catch {}
  }, []);

  const estaMutado = useCallback(() => {
    try { return localStorage.getItem("toalugando_som_off") === "1"; }
    catch { return false; }
  }, []);

  // CRITICO: retorno MEMOIZADO. Antes era objeto literal novo a cada
  // render -> qualquer useCallback/useEffect que dependia de `som`
  // (HomeLocador.fetchDados, hub global, TelaRastreamento) era recriado
  // a cada render -> effect rodava -> setState -> re-render -> LOOP
  // INFINITO ("Maximum update depth exceeded") -> ErrorBoundary global
  // derrubava o app com "Algo deu errado" (pior no painel do locador).
  return useMemo(() => ({ tocar, mute, estaMutado }), [tocar, mute, estaMutado]);
};
